<template>
  <LayoutButton
    :id="popoverId"
    icon="ph-calendar-blank"
  >
    {{ label }}
  </LayoutButton>
  <BPopover
    ref="popover"
    placement="bottom"
    :target="popoverId"
    triggers="click blur"
  >
    <template #title>Selecionar período</template>

    <div
      class="pt-2 pb-3"
      style="width: 248px"
    >
      <DatePicker
        :calendar-button="false"
        :clearable="false"
        label="Data Inicial"
        :value="localStart"
        @input="onStartChange"
      />
      <DatePicker
        :calendar-button="false"
        :clearable="false"
        label="Data Final"
        :value="localEnd"
        @input="onEndChange"
      />
    </div>

    <div class="presets mb-3">
      <BButton
        pill
        size="sm"
        variant="outline-primary"
        @click="applyPreset('today')"
      >
        Hoje
      </BButton>
      <BButton
        pill
        size="sm"
        variant="outline-primary"
        @click="applyPreset('yesterday')"
      >
        Ontem
      </BButton>
      <BButton
        pill
        size="sm"
        variant="outline-primary"
        @click="applyPreset('todayYesterday')"
      >
        Hoje e ontem
      </BButton>
    </div>
    <div class="presets mb-3">
      <BButton
        pill
        size="sm"
        variant="outline-primary"
        @click="applyPreset('week')"
      >
        1 semana
      </BButton>
      <BButton
        pill
        size="sm"
        variant="outline-primary"
        @click="applyPreset('month')"
      >
        1 mês
      </BButton>
      <BButton
        pill
        size="sm"
        variant="outline-primary"
        @click="applyPreset('sixMonths')"
      >
        6 meses
      </BButton>
    </div>

    <LayoutButton
      block
      icon="ph-funnel-simple"
      variant="primary"
      @click="onSubmit"
    >
      Filtrar
    </LayoutButton>
  </BPopover>
</template>

<script setup lang="ts">
import { BButton, BPopover } from "bootstrap-vue";
import { localDateISO } from "@/globals/methods";
import { ref, useId, watch } from "vue";
import DatePicker from "@/components/comum/StringDatePicker.vue";
import LayoutButton from "@/components/comum/buttons/LayoutButton.vue";

const props = withDefaults(
  defineProps<{
    filterKey?: string;
    label?: string;
    value?: string | null;
  }>(),
  { filterKey: "periodo", label: "Data", value: null },
);

const emit = defineEmits<{
  update: [payload: { key: string; label: string; value: string; valueName: string }];
}>();

const popoverId = `date-range-filter-${useId()}`;
const popover = ref();

const localStart = ref<string | undefined>(props.value?.split(",")[0] ?? undefined);
const localEnd = ref<string | undefined>(props.value?.split(",")[1] ?? undefined);

watch(
  () => props.value,
  (v) => {
    localStart.value = v?.split(",")[0] ?? undefined;
    localEnd.value = v?.split(",")[1] ?? undefined;
  },
);

function today(): string {
  return localDateISO(new Date());
}

function offsetDays(date: string, days: number): string {
  const d = new Date(date + "T12:00:00");
  d.setDate(d.getDate() + days);
  return localDateISO(d);
}

function offsetMonths(date: string, months: number): string {
  const d = new Date(date + "T12:00:00");
  d.setMonth(d.getMonth() + months);
  return localDateISO(d);
}

function formatBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function emitRange(start: string, end: string) {
  const valueName = start === end ? formatBR(start) : `${formatBR(start)} – ${formatBR(end)}`;
  emit("update", { key: props.filterKey, label: "Período", value: `${start},${end}`, valueName });
}

function onStartChange(v: string | null) {
  if (!v) return;
  localStart.value = v;
  if (localEnd.value && v > localEnd.value) localEnd.value = v;
}

function onEndChange(v: string | null) {
  if (!v) return;
  localEnd.value = v;
  if (localStart.value && v < localStart.value) localStart.value = v;
}

function onSubmit() {
  if (localStart.value && localEnd.value) {
    emitRange(localStart.value, localEnd.value);
  }
  popover.value?.$emit("close");
}

function applyPreset(preset: "today" | "yesterday" | "todayYesterday" | "week" | "month" | "sixMonths") {
  const t = today();

  switch (preset) {
    case "today":
      localStart.value = localEnd.value = t;
      break;
    case "yesterday": {
      const y = offsetDays(t, -1);
      localStart.value = localEnd.value = y;
      break;
    }
    case "todayYesterday":
      localStart.value = offsetDays(t, -1);
      localEnd.value = t;
      break;
    case "week":
      localStart.value = offsetDays(t, -7);
      localEnd.value = t;
      break;
    case "month":
      localStart.value = offsetDays(t, -30);
      localEnd.value = t;
      break;
    case "sixMonths":
      localStart.value = offsetMonths(t, -6);
      localEnd.value = t;
      break;
  }

  emitRange(localStart.value!, localEnd.value!);
  popover.value?.$emit("close");
}
</script>

<style scoped>
.presets .btn {
  margin: 0;
}
::v-deep .form-group {
  margin-bottom: 0.25rem;
}
::v-deep .form-label {
  font-weight: normal;
  margin-bottom: 0;
}
</style>
